import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Typography, getDepartment } from '@glplus/glpweb-dsl';
import { categoryLabel as taxonomyCategoryLabel } from '@glplus/design-tokens';
import { VendorApiError, type ProductStatus } from '../../lib/vendorApiClient';

export const HEADING = '#303A42';
export const MUTED = '#5A7063';
export const BORDER = '#E5E5E5';
export const ORANGE_INK = '#A85200';
export const GREEN_INK = '#1F7026';
export const RED = '#C62828';

/**
 * One chip per product state (Figma P1). Live is the only green one; the rest
 * say whose turn it is. Recalled, on sale and out of stock are separate flags.
 */
const STATUS: Record<ProductStatus, { bg: string; fg: string; border?: string }> = {
  live: { bg: '#EAF5EA', fg: GREEN_INK },
  hidden: { bg: '#F1F2F4', fg: '#3C4650' },
  not_sold_here: { bg: '#F1F2F4', fg: '#3C4650' },
  location_pending: { bg: '#FEF1E7', fg: ORANGE_INK },
  needs_answer: { bg: '#FEF1E7', fg: ORANGE_INK, border: ORANGE_INK },
  with_us: { bg: '#EEF1F8', fg: '#343F5B' },
  stuck: { bg: '#FFFFFF', fg: RED, border: RED },
  verifying: { bg: '#FEF1E7', fg: ORANGE_INK },
  returned: { bg: '#FFFFFF', fg: RED, border: RED },
};

export function ProductStatusChip({ status, size = 'small' }: { status: ProductStatus; size?: 'small' | 'medium' }) {
  const { t } = useTranslation();
  const s = STATUS[status];
  return (
    <Chip
      label={`● ${t(`products.status.${status}`)}`}
      size={size}
      backgroundColor={s.bg}
      textColor={s.fg}
      fontSize={12}
      fontWeight={700}
      sx={s.border ? { border: `1px solid ${s.border}` } : undefined}
    />
  );
}

export function FlagChip({ kind }: { kind: 'on_sale' | 'recalled' | 'out_of_stock' }) {
  const { t } = useTranslation();
  const spec =
    kind === 'recalled'
      ? { bg: RED, fg: '#FFFFFF' }
      : kind === 'on_sale'
        ? { bg: '#FEF1E7', fg: ORANGE_INK }
        : { bg: '#F1F2F4', fg: '#3C4650' };
  return <Chip label={t(`products.flag.${kind}`)} size="small" backgroundColor={spec.bg} textColor={spec.fg} fontSize={12} fontWeight={700} />;
}

/** The department name in the reader's language; the stored name when we have no translation. */
export function departmentLabel(t: TFunction, name: string): string {
  const def = getDepartment(name);
  return def?.languageKey ? t(def.languageKey, { defaultValue: name }) : name;
}

/** A category in the reader's language; one we do not recognise is shown as stored. */
export function categoryLabel(lang: string, department: string | null | undefined, category: string | null | undefined): string | null {
  if (!category) return null;
  return taxonomyCategoryLabel(department, category, lang);
}

/** "Department › Category" in the reader's language. */
export function departmentPath(t: TFunction, lang: string, department: string | null | undefined, ...rest: (string | null | undefined)[]): string {
  const [category, ...more] = rest;
  return [department ? departmentLabel(t, department) : null, categoryLabel(lang, department, category), ...more].filter(Boolean).join(' › ');
}

/** A coded vendor-api error in the reader's language; anything else as the server worded it. */
export function describeProductError(e: unknown, t: TFunction): string {
  if (e instanceof VendorApiError && e.code) {
    return t(`products.errors.${e.code}`, { defaultValue: e.message });
  }
  return e instanceof Error && e.message ? e.message : t('products.errors.generic');
}

export function PageTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <Box>
      <Typography component="h1" sx={{ fontSize: 40, lineHeight: 1.2, fontWeight: 700, color: HEADING }}>
        {title}
      </Typography>
      {subtitle && <Typography sx={{ mt: 0.5, fontSize: 16, color: MUTED }}>{subtitle}</Typography>}
    </Box>
  );
}

/** A grey square where a product has no photo of ours yet. */
export function ProductThumb({ src, size = 44 }: { src: string | null; size?: number }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '8px',
        bgcolor: '#E9EBEF',
        backgroundImage: src ? `url("${src}")` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    />
  );
}

export function FieldLabel({ children }: { children: string }) {
  return (
    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: MUTED, textTransform: 'uppercase' }}>
      {children}
    </Typography>
  );
}
